<template>
  <Transition name="global-loader-fade">
    <div
      v-if="active"
      class="global-preloader"
      role="status"
      aria-live="polite"
      aria-busy="true"
      :aria-label="message || 'Loading'"
    >
      <div class="global-preloader__backdrop" aria-hidden="true" />

      <div class="global-preloader__panel">
        <div class="global-preloader__logo-wrap" aria-hidden="true">
          <img
            class="global-preloader__logo global-preloader__logo--light"
            src="@/assets/ipalogo.png"
            alt=""
          />
          <img
            class="global-preloader__logo global-preloader__logo--dark"
            src="@/assets/ipalogo_dark.png"
            alt=""
          />
        </div>

        <div class="global-preloader__sport" aria-hidden="true">
          <Transition name="sport-icon" mode="out-in">
            <i :key="currentIcon" :class="['fa-solid', currentIcon, 'global-preloader__icon']" />
          </Transition>
        </div>

        <p v-if="displayMessage" class="global-preloader__message">{{ displayMessage }}</p>
      </div>
    </div>
  </Transition>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";

/** Sport icons cycle while loading (Font Awesome 6). */
const SPORT_ICONS = [
  "fa-futbol",
  "fa-basketball",
  "fa-volleyball",
  "fa-table-tennis-paddle-ball",
  "fa-rugby-ball",
  "fa-person-running",
  "fa-dumbbell",
];

const ICON_INTERVAL_MS = 900;
export default {
  name: "GlobalPreloader",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const currentIndex = ref(0);
    let intervalId = null;

    const currentIcon = computed(
      () => SPORT_ICONS[currentIndex.value % SPORT_ICONS.length],
    );

    const displayMessage = computed(() => (props.message || "").trim());

    function startCycle() {
      if (intervalId != null) return;
      intervalId = setInterval(() => {
        currentIndex.value = (currentIndex.value + 1) % SPORT_ICONS.length;
      }, ICON_INTERVAL_MS);
    }

    function stopCycle() {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    onMounted(startCycle);
    onUnmounted(stopCycle);

    return {
      currentIcon,
      displayMessage,
    };
  },
};
</script>

<style scoped>
.global-preloader {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

.global-preloader__backdrop {
  position: absolute;
  inset: 0;
  background: var(--light-overlay);
  backdrop-filter: blur(8px);
}

.global-preloader__panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 2.5rem 2rem;
  max-width: min(90vw, 360px);
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
  border-left: 4px solid var(--border-color-hover);
  border-radius: 16px;
  box-shadow: 0 20px 60px var(--box-shadow);
}

.global-preloader__logo-wrap {
  position: relative;
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.global-preloader__logo {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.35s ease;
}

.global-preloader__logo--light {
  opacity: 1;
}

.global-preloader__logo--dark {
  opacity: 0;
}

[data-theme="dark"] .global-preloader__logo--light {
  opacity: 0;
}

[data-theme="dark"] .global-preloader__logo--dark {
  opacity: 1;
}

.global-preloader__sport {
  width: 4.5rem;
  height: 4.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-color-hover);
}

.global-preloader__icon {
  font-size: 2.75rem;
  filter: drop-shadow(0 0 12px rgba(0, 151, 0, 0.35));
}

.global-preloader__message {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--subtext-color);
  text-align: center;
  max-width: 280px;
  line-height: 1.45;
}

/* Transitions */
.global-loader-fade-enter-active,
.global-loader-fade-leave-active {
  transition: opacity 0.35s ease;
}

.global-loader-fade-enter-from,
.global-loader-fade-leave-to {
  opacity: 0;
}

.sport-icon-enter-active,
.sport-icon-leave-active {
  transition: opacity 0.25s ease, transform 0.35s ease;
}

.sport-icon-enter-from {
  opacity: 0;
  transform: scale(0.85) rotate(-8deg);
}

.sport-icon-leave-to {
  opacity: 0;
  transform: scale(1.05) rotate(8deg);
}
</style>